import { Dialog } from "@kobalte/core/dialog";
import { CreateViewRequestConfig, useStorytellClient } from "@storytell/client";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbApps, TbBook, TbFileText, TbFilter, TbFilterOff, TbMessage2, TbStack, TbX } from "solid-icons/tb";
import {
  type Accessor,
  type Setter,
  createEffect,
  createSignal,
  For,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
} from "solid-js";
import { useWire } from "~/wire/WireProvider";
import { AssetsFilterView } from "../filtering/AssetsFilterView";
import {
  type FilterBuilder,
  serverFiltersToUiFilters,
  uiFiltersToServerFilters,
  useFilterBuilder,
} from "../filtering/filterBuilder";
import type { Asset, AssetId, Concept, ConceptId, ViewId } from "@storytell/types";
import { twMerge } from "tailwind-merge";
import { NavigationAssetsPane } from "../layouts/minimal/NavigationAssetsPane";
import { TextField } from "@kobalte/core/text-field";
import { TbStackPlus } from "./TbStackPlus";
import { NavigationConceptsPane } from "../concepts/NavigationConceptsPane";
import { type EntitySelection, useEntitySelection } from "./useEntitySelection";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import dayjs from "dayjs";

export const KnowledgePane = () => {
  const wire = useWire();
  const { knowledgeUi, sync: s } = wire;

  const [search, setSearch] = createSignal("");

  const [filterExpanded, setFilterExpanded] = createSignal(false);

  const assetsSelection = useEntitySelection();
  const conceptsSelection = useEntitySelection();

  const [selectedConcept, setSelectedConcept] = createSignal<ConceptId | "">("");

  const hasViewChanges = () =>
    knowledgeUi.activeView() === "all" &&
    // assetsSelection.selectedIds().length > 0 ||
    //   conceptsSelection.selectedIds().length > 0 ||
    filterBuilder.state.filter[0]?.filters.length > 0;

  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd/Ctrl + Shift + K
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (knowledgeUi.isOpen()) {
          knowledgeUi.close();
        } else {
          knowledgeUi.open();
        }
      }
    };
    const unsubscribeAddItemsToPromptScope = subscribeToGlobalEvent("add-items-to-prompt-scope", (e) => {
      knowledgeUi.close();
    });
    window.addEventListener("keydown", handleKeyDown);
    onCleanup(() => {
      window.removeEventListener("keydown", handleKeyDown);
      unsubscribeAddItemsToPromptScope();
    });
  });

  const filterBuilder = useFilterBuilder<Asset | Concept>();

  const [screen, setScreen] = createSignal("default" as "default" | "newView");
  createEffect(() => {
    if (!knowledgeUi.isOpen()) {
      setScreen("default");
    }
  });

  createEffect(() => {
    if (knowledgeUi.activeView() === "all") {
      filterBuilder.resetFilter();
    } else {
      const view = wire.sync.views.get(knowledgeUi.activeView() as ViewId);
      if (view) {
        filterBuilder.setState({ filter: serverFiltersToUiFilters<Asset | Concept>(wire, view.filters) });
      }
    }
  });

  return (
    <AnimateChildren
      show={knowledgeUi.isOpen()}
      enter={{ opacity: [0, 1], x: [30, 0] }}
      exit={{ opacity: [1, 0], x: [0, 30] }}
      duration={150}
    >
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) {
              knowledgeUi.close();
            }
          }}
        >
          <Dialog.Portal>
            <AnimateChildren
              show={anim.isVisible()}
              enter={{ opacity: [0, 1] }}
              exit={{ opacity: [1, 0] }}
              duration={150}
            >
              {(sAnim) => (
                <Dialog.Overlay
                  ref={sAnim.ref}
                  class="fixed z-50 inset-0 bg-black/30 dark:bg-white/10 animate-fade-in-quick"
                />
              )}
            </AnimateChildren>
            <div ref={anim.ref} class="fixed z-50 inset-0 flex justify-end">
              <Dialog.Content class="space-y-2 py-2 my-2 min-w-[70%] max-h-[calc(100vh-1rem)] mr-2 text-primary-text max-w-xl shadow-2xl bg-panel overflow-y-auto rounded-xl">
                <Switch>
                  <Match when={screen() === "default"}>
                    <KnowledgePaneContent
                      selectedConcept={selectedConcept()}
                      setSelectedConcept={setSelectedConcept}
                      hasViewChanges={hasViewChanges}
                      onNewView={() => setScreen("newView")}
                      filterBuilder={filterBuilder}
                      assetsSelection={assetsSelection}
                      conceptsSelection={conceptsSelection}
                      search={search}
                      setSearch={setSearch}
                      filterExpanded={filterExpanded}
                      setFilterExpanded={setFilterExpanded}
                    />
                  </Match>
                  <Match when={screen() === "newView"}>
                    <div class="flex items-center justify-between px-2 border-b border-primary-border pb-2">
                      <div class="flex items-center gap-2">Creating a new View [Learn More]</div>
                      <Dialog.CloseButton as={StButton} class="">
                        <TbX />
                      </Dialog.CloseButton>
                    </div>

                    <div class="px-2">
                      <TextField class="flex items-center gap-4">
                        <TextField.Label>Name</TextField.Label>
                        <TextField.Input
                          class="w-64 px-3 py-1 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                          placeholder="Search"
                        />
                      </TextField>
                    </div>

                    <div class="px-2">
                      <h3>Filters:</h3>

                      <AssetsFilterView builder={filterBuilder} />

                      <h3>Selected Assets:</h3>
                      <For each={assetsSelection.selectedIds()}>
                        {(id) => (
                          <div class="flex items-center gap-2">
                            <TbFileText />
                            {wire.sync.assets.get(id as AssetId)?.fileName}
                          </div>
                        )}
                      </For>

                      <h3>Selected Concepts:</h3>
                      <For each={conceptsSelection.selectedIds()}>
                        {(id) => (
                          <div class="flex items-center gap-2">
                            <TbBook />
                            {wire.sync.concepts.get(id as ConceptId)?.name}
                          </div>
                        )}
                      </For>
                    </div>
                  </Match>
                </Switch>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};

export const KnowledgePaneViews = (props: {
  filterBuilder: FilterBuilder<Asset | Concept>;
  assetsSelection: EntitySelection;
  conceptsSelection: EntitySelection;
  hasViewChanges: () => boolean;
  onNewView: () => void;
}) => {
  const wire = useWire();
  const { sync: s, knowledgeUi } = wire;

  const newViewWith = () => {
    const selectedAssets = props.assetsSelection.selectedIds().length;
    const selectedConcepts = props.conceptsSelection.selectedIds().length;
    const filters = props.filterBuilder.state.filter[0]?.filters.length ? props.filterBuilder.state.filter.length : 0;
    if (!selectedAssets && !selectedConcepts && !filters) return "";

    const items: string[] = [];
    if (filters) items.push(`${filters} filter${filters === 1 ? "" : "s"}`);
    if (selectedAssets) items.push(`${selectedAssets} asset${selectedAssets === 1 ? "" : "s"}`);
    if (selectedConcepts) items.push(`${selectedConcepts} concept${selectedConcepts === 1 ? "" : "s"}`);

    return ` (from: ${items.join(", ")})`;
  };

  const isActive = (id: "all" | ViewId | "new") => {
    if (id === "all" && knowledgeUi.activeView() === "all") return !props.hasViewChanges();
    if (id === "new" && knowledgeUi.activeView() === "all") return props.hasViewChanges();
    return knowledgeUi.activeView() === id;
  };

  const [createViewOpen, setCreateViewOpen] = createSignal(false);

  return (
    <>
      <For
        each={[
          { id: "all", name: "All Knowledge" } as const,
          ...s.views
            .search("")
            .sort((a, b) => dayjs(b.modifiedAt || b.createdAt).diff(dayjs(a.modifiedAt || a.createdAt))),
        ]}
      >
        {(view) => (
          <StButton
            data-active={isActive(view.id)}
            iconClass="size-5"
            onClick={() => knowledgeUi.setActiveView(view.id)}
            noColor={isActive(view.id)}
            icon={view.id === "all" ? TbBook : TbStack}
            class={twMerge(
              "py-0.5 dark:border-primary-border shrink-0",
              isActive(view.id) && "bg-brand hover:bg-brand-hover text-on-brand",
            )}
          >
            {view.name}
          </StButton>
        )}
      </For>

      <Show when={isActive("new")}>
        <StPopover
          noArrow
          gutter={10}
          placement="bottom"
          contentProps={{ class: "z-50" }}
          open={createViewOpen()}
          onOpenChange={setCreateViewOpen}
          content={
            <form
              class="w-sm flex flex-col gap-4 p-4"
              onSubmit={async (e) => {
                e.preventDefault();
                const data = new FormData(e.target as HTMLFormElement);
                const name = data.get("viewName")?.toString();
                if (!name) return;

                const client = useStorytellClient();

                const request = CreateViewRequestConfig.withBearerToken(wire.auth.token());

                const response = await client.fetch(request, undefined, {
                  name: name,
                  description: "",
                  filters: uiFiltersToServerFilters(props.filterBuilder.state.filter),
                });

                if (response.isOk()) {
                  wire.sync.views.set(response.value.id, { ...response.value, userId: wire.auth.user()?.id || "" });
                  knowledgeUi.setActiveView(response.value.id);
                }

                setCreateViewOpen(false);
              }}
            >
              <div class="flex flex-col gap-2">
                <label for="view-name" class="mt-1 text-sm text-secondary-text">
                  View Name
                </label>
                <TextField required>
                  <TextField.Input
                    id="view-name"
                    name="viewName"
                    class="w-full px-3 py-1 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder=""
                  />
                </TextField>
              </div>

              <Show when={props.hasViewChanges()}>
                <div class="">
                  <p class="mb-2">You are creating a new view with:</p>
                  <ul class="list-disc list-inside pl-3">
                    <Show when={props.filterBuilder.state.filter.reduce((acc, curr) => acc + curr.filters.length, 0)}>
                      {(len) => (
                        <li>
                          {len()} label{len() === 1 ? "" : "s"} filter
                          {len() === 1 ? "" : "s"}
                        </li>
                      )}
                    </Show>
                    <Show when={props.assetsSelection.selectedIds().length}>
                      {(len) => (
                        <li>
                          {len()} selected Asset{len() === 1 ? "" : "s"}
                        </li>
                      )}
                    </Show>
                    <Show when={props.conceptsSelection.selectedIds().length}>
                      {(len) => (
                        <li>
                          {len()} selected Concept{len() === 1 ? "" : "s"}
                        </li>
                      )}
                    </Show>
                  </ul>
                </div>
              </Show>

              <div class="flex justify-end">
                <StButton type="submit">Create View</StButton>
              </div>
            </form>
          }
        >
          <StPopoverTrigger
            as={StButton}
            data-active={isActive("new")}
            icon={TbStackPlus}
            iconClass="size-5"
            noBorder
            noColor
            class={twMerge(
              "py-0.5 dark:border-primary-border shrink-0 sticky right-0",
              isActive("new") && "bg-brand hover:bg-brand-hover text-on-brand",
            )}
          >
            New View{isActive("new") ? "* - save" : ""}
          </StPopoverTrigger>
        </StPopover>
      </Show>
    </>
  );
};

export const KnowledgePaneContent = (props: {
  onNewView: () => void;
  filterBuilder: FilterBuilder<Asset | Concept>;
  assetsSelection: EntitySelection;
  conceptsSelection: EntitySelection;
  search: Accessor<string>;
  setSearch: Setter<string>;
  filterExpanded: Accessor<boolean>;
  setFilterExpanded: Setter<boolean>;
  hasViewChanges: () => boolean;
  selectedConcept: ConceptId | "";
  setSelectedConcept: (conceptId: ConceptId | "") => void;
}) => {
  const wire = useWire();
  const { knowledgeUi } = wire;

  return (
    <>
      <Show when={props.selectedConcept === ""}>
        <div class="flex items-center justify-between px-2 border-b border-primary-border pb-2 max-w-full">
          <div class="flex items-center gap-2 flex-auto overflow-x-auto scrollbar-hide">
            <KnowledgePaneViews
              hasViewChanges={props.hasViewChanges}
              onNewView={props.onNewView}
              filterBuilder={props.filterBuilder}
              assetsSelection={props.assetsSelection}
              conceptsSelection={props.conceptsSelection}
            />
          </div>
          <Dialog.CloseButton as={StButton} class="shrink-0">
            <TbX />
          </Dialog.CloseButton>
        </div>

        <div class="px-2 flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <Show when={!props.hasViewChanges()}>
              <button
                type="button"
                onClick={() => {
                  dispatchGlobalEvent("add-items-to-prompt-scope", {
                    ids: knowledgeUi.activeView() !== "all" ? [knowledgeUi.activeView() as ViewId] : [],
                    allKnowledge: knowledgeUi.activeView() === "all",
                  });
                }}
                class="group/btn relative text-left cursor-pointer font-medium text-primary-text max-w-full line-clamp-1 wrap-anywhere py-1 px-2 border border-transparent rounded-lg hover:border-primary-border transition-all"
              >
                <span class="absolute left-2 opacity-0 group-hocus/btn:opacity-100 transition-all duration-100">
                  Chat with
                </span>
                <span class="group-hocus/btn:ml-19 transition-all duration-100 group-hocus/btn:text-transparent group-hocus/btn:bg-gradient-to-r group-hocus/btn:from-violet-600 group-hocus/btn:to-blue-600 group-hocus/btn:dark:from-violet-400 group-hocus/btn:dark:to-blue-400 group-hocus/btn:bg-clip-text">
                  <span class="hidden group-hocus/btn:inline">@</span>
                  {knowledgeUi.activeView() === "all"
                    ? "All Knowledge"
                    : wire.sync.views.get(knowledgeUi.activeView() as ViewId)?.name}
                </span>
              </button>
            </Show>
            <StButton
              icon={props.filterExpanded() ? TbFilterOff : TbFilter}
              class="dark:border-primary-border p-1"
              onClick={() => props.setFilterExpanded((s) => !s)}
            >
              {props.filterExpanded()
                ? "Hide filters"
                : props.filterBuilder.state.filter[0]?.filters.length > 0
                  ? `${props.filterBuilder.state.filter.length} filter${props.filterBuilder.state.filter.length === 1 ? "" : "s"}`
                  : "Filter"}
            </StButton>

            {/* <StButton
          icon={searchExpanded() ? TbSearchOff : TbSearch}
          class="dark:border-primary-border p-1"
          onClick={() => setSearchExpanded((s) => !s)}
        /> */}

            <Show when={props.hasViewChanges() && knowledgeUi.activeView() !== "all"}>
              <StButton
                icon={TbMessage2}
                onClick={() => {
                  dispatchGlobalEvent("add-items-to-prompt-scope", {
                    ids: knowledgeUi.activeView() !== "all" ? [knowledgeUi.activeView() as ViewId] : [],
                    allKnowledge: knowledgeUi.activeView() === "all",
                  });
                }}
                class="shrink-0 bg-brand hover:bg-brand-hover text-on-brand"
              >
                Chat with this View
              </StButton>
            </Show>
          </div>
        </div>
        <Show when={props.filterExpanded()}>
          <div class="px-2">
            <AssetsFilterView builder={props.filterBuilder} />
          </div>
        </Show>
        <div class="px-4 flex items-center justify-between">
          <div class="flex items-center gap-4">
            <span class="text-sm">Viewing as</span>

            <div class="flex items-center gap-2 relative">
              <div
                class="absolute left-0 top-0 bottom-0 w-24 bg-tile rounded-lg transition-all duration-200 border border-primary-border"
                style={{
                  transform: `translateX(${knowledgeUi.activeTab() === "concepts" ? 0 : 96 + 1 + 2 * 8}px)`,
                }}
              />
              <For each={[{ id: "concepts", icon: TbApps }, "|", { id: "assets", icon: TbFileText }] as const}>
                {(tab) => (
                  <Show when={tab !== "|" && tab} fallback={<div class="w-px h-3 bg-primary-border" />}>
                    {(tab) => (
                      <StButton
                        onClick={() => knowledgeUi.setActiveTab(tab().id)}
                        data-active={tab().id === knowledgeUi.activeTab()}
                        icon={tab().icon}
                        noColor
                        noBorder
                        class="capitalize focus:ring-0 relative w-24 data-[active=true]:font-semibold"
                      >
                        {tab().id}
                      </StButton>
                    )}
                  </Show>
                )}
              </For>
            </div>
          </div>
          <TextField value={props.search()} onChange={props.setSearch}>
            <TextField.Label class="sr-only">Search</TextField.Label>
            <TextField.Input
              class="w-64 px-3 py-1 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="Search"
            />
          </TextField>
        </div>
      </Show>

      <div class="px-2">
        <Switch>
          <Match when={knowledgeUi.activeTab() === "concepts"}>
            <NavigationConceptsPane
              selectedConcept={props.selectedConcept}
              setSelectedConcept={props.setSelectedConcept}
              selection={props.conceptsSelection}
              search={props.search()}
              buildFilterFunction={props.filterBuilder.buildFilterFunction()}
            />
          </Match>
          <Match when={knowledgeUi.activeTab() === "assets"}>
            <NavigationAssetsPane
              selection={props.assetsSelection}
              search={props.search()}
              setSearch={props.setSearch}
              buildFilterFunction={props.filterBuilder.buildFilterFunction()}
              setLabelFilter={() => {}}
              assetToHighlight=""
              setAssetToHighlight={() => {}}
              labelToFilterBy=""
              setLabelToFilterBy={() => {}}
            />
          </Match>
        </Switch>
      </div>
    </>
  );
};
